/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {
    AffectedLotLabelExpressionBuilder,
    AngleLabelExpressionBuilderService,
    ConstructLineLabelExpressionBuilderService,
    ControlPointLabelExpressionBuilderService,
    EnlargementLabelExpressionBuilderService,
    GeneralAnnotationLabelExpressionBuilderService,
    InterestLabelExpressionBuilderService,
    LineLabelExpressionBuilderService,
    LotLabelExpressionBuilderService,
    PointLabelExpressionBuilderService,
    RoadlabelExpressionBuilderService
} from './builder/label-expression-builder.index';
import {LookupService} from '../../../common/service/lookup.service';
import {LabelExpressionUtilityService} from './label-expression-utility-service';
import {labelProperties, Attributes} from '../../../common/models/label-expression.model';

@Injectable({
    providedIn: 'root'
})
export class LabelExpressionService {
    title = 'LabelExpressionService';

    constructor(
        private affectedLotLabelExpressionBuilder: AffectedLotLabelExpressionBuilder,
        private angleLabelExpressionBuilder: AngleLabelExpressionBuilderService,
        private constructLineLabelExpressionBuilder: ConstructLineLabelExpressionBuilderService,
        private controlPointLabelExpressionBuilder: ControlPointLabelExpressionBuilderService,
        private enlargementLabelExpressionBuilder: EnlargementLabelExpressionBuilderService,
        private generalAnnotationLabelExpressionBuilder: GeneralAnnotationLabelExpressionBuilderService,
        private interestLabelExpressionBuilder: InterestLabelExpressionBuilderService,
        private lineLabelExpressionBuilder: LineLabelExpressionBuilderService,
        private lotLabelExpressionBuilder: LotLabelExpressionBuilderService,
        private pointLabelExpressionBuilder: PointLabelExpressionBuilderService,
        private roadLabelExpressionBuilder: RoadlabelExpressionBuilderService,
        private labelExpressionUtilityService: LabelExpressionUtilityService,
        private lookupService: LookupService
    ) {}

    getBuilder(attrs: Attributes) {
        switch (attrs.featuretype) {
            case 'constructLine':
                return this.constructLineLabelExpressionBuilder;
            case 'constructPoint':
                return this.pointLabelExpressionBuilder;
            case 'enlargement':
                return this.enlargementLabelExpressionBuilder;
            case 'general':
                return this.generalAnnotationLabelExpressionBuilder;
            case 'interest':
                return this.interestLabelExpressionBuilder;
            case 'line':
                return this.lineLabelExpressionBuilder;
            case 'lot':
                return this.lotLabelExpressionBuilder;
            case 'road':
                return this.roadLabelExpressionBuilder;
            case 'affectedLot':
                return this.affectedLotLabelExpressionBuilder;
            case 'point':
                return attrs.featuresubtype <= 5 || attrs.featuresubtype === 100
                    ? this.controlPointLabelExpressionBuilder
                    : this.pointLabelExpressionBuilder;
        }

        switch (attrs.type) {
            case 12:
                return this.angleLabelExpressionBuilder;
            case 13:
                return this.angleLabelExpressionBuilder;
            default:
                return null;
        }
    }

    create(attributes: Attributes, polygons?: any) {
        const labelExpressionBuilder = this.getBuilder(attributes);

        if (!labelExpressionBuilder) {
            console.debug(this.title + ': featuretype ' + attributes.featuretype + 'not supported');
            return null;
        }

        const labelLookup = this.getAnnotationLabelExpression(attributes.featuretype, attributes.featuresubtype);
        if (labelLookup.length > 0) {
            if (labelLookup.length > 1) {
                console.log(this.title + ': featureType ' + attributes.featuretype + ' has multiple label lookups');
            }
            if (labelLookup[0].constantLabelExpressionFormat) {
                return this.setLabelExpression(labelLookup[0].constantLabelExpressionFormat, attributes);
            } else {
                return labelExpressionBuilder.build(attributes, polygons);
            }
        } else {
            return labelExpressionBuilder.build(attributes, polygons);
        }
    }

    getAnnotationLabelExpression(featureType: string, subType: number) {
        return this.lookupService
            .getLookups('annotationLabelExpressions')
            .filter((l: any) => l.featureType === featureType && l.featureSubTypes.split(',').some((v: string) => v === `${subType}`));
    }

    setLabelExpression(expression: any, attributes: Attributes) {
        const split = expression.split('$');
        const labels = [];
        const labelProperties = {} as labelProperties;

        split[split.length - 1] = split[split.length - 1].replace('</FNT>', '');
        expression = split[0];

        for (let i = 1; i < split.length; i++) {
            const att = 'attribute' + i;
            if (Object.hasOwn(attributes, att)) {
                split[i] = this.sanitizeLabel(split[i], attributes[att]);
            } else {
                split[i] = null;
            }
        }

        let exist = false;
        for (let j = 1; j < split.length; j++) {
            if (split[j]) {
                exist = true;
                if (split[j].startsWith('<FNT')) {
                    if (split[j].endsWith('\n')) {
                        // eslint-disable-next-line no-control-regex
                        expression = expression.replace(new RegExp('\n$'), '</FNT>\n') + split[j];
                    } else {
                        expression += '</FNT>' + split[j];
                    }
                } else {
                    expression += split[j];
                }
            }
        }
        if (!exist) {
            return null;
        }

        // eslint-disable-next-line no-control-regex
        expression = expression.replace(new RegExp('\n$'), '');
        if (!expression.endsWith('</FNT>')) {
            expression += '</FNT>';
        }

        const fontSize = this.executeRegex(/'.size.*'/gi, expression);
        const fontStyle = this.executeRegex(/'.style.*'/gi, expression);

        labels.push({
            style: {
                fontSize: `${fontSize}em`,
                fontStyle: `${fontStyle}`,
                textAnchor: 'middle'
            },
            value: `${split[1]}`
        });

        labelProperties.labels = labels;
        labelProperties.labelExpression = expression;

        return labelProperties;
    }

    executeRegex(regex: any, textToSearch: any) {
        let m: any;
        let results = undefined;
        while ((m = regex.exec(textToSearch)) !== null) {
            if (m.index === regex.lastIndex) {
                regex.lastIndex++;
            }

            m.forEach((match: string) => {
                results = parseInt(match.split('=')[1].replace(/^'(.*)'$/, '$1')) || '';
            });
        }
        return results;
    }

    sanitizeLabel(expression: any, attribute: Attributes) {
        if (!attribute) {
            return null;
        } else {
            expression = expression.replace('{', '');
            expression = expression.replace('}', '');
            const split = expression.split(':');
            const sanitizedExpression = ['', '', ''];
            sanitizedExpression[1] = this.labelExpressionUtilityService.encodeSpecialCharacters(attribute);

            const modifiers: string[] = split[1].split(',');

            modifiers.forEach(mod => {
                switch (true) {
                    case mod.indexOf('FONT') !== -1:
                        {
                            const font = mod.split('_');
                            if (sanitizedExpression[0].indexOf('<FNT') !== -1) {
                                sanitizedExpression[0] = sanitizedExpression[0] + " name='" + font[1] + "'";
                            } else {
                                sanitizedExpression[0] = "<FNT name='" + font[1] + "'";
                            }
                        }
                        return;
                    case mod.indexOf('SIZE') !== -1:
                        {
                            const size = mod.split('_');
                            if (sanitizedExpression[0].indexOf('<FNT') !== -1) {
                                sanitizedExpression[0] = sanitizedExpression[0] + " size='" + size[1] + "'";
                            } else {
                                sanitizedExpression[0] = "<FNT size='" + size[1] + "'";
                            }
                        }
                        return;
                    case mod.indexOf('UPPER') !== -1:
                        sanitizedExpression[1] = sanitizedExpression[1].toUpperCase();
                        return;
                    case mod.indexOf('SPACE') !== -1:
                        sanitizedExpression[1] += String('   ');
                        return;
                    case mod.indexOf('ITALIC') !== -1:
                        sanitizedExpression[1] = '<ITA>' + sanitizedExpression[1] + '</ITA>';
                        return;
                    case mod.indexOf('NEW_LINE') !== -1:
                        sanitizedExpression[2] = String('\n');
                        return;
                    default:
                        console.info(this.title + ': unknown label modifier, ' + mod);
                }
            });

            if (sanitizedExpression[0].indexOf('<FNT') !== -1) {
                sanitizedExpression[0] = sanitizedExpression[0] + '>';
            }

            return sanitizedExpression[0] + sanitizedExpression[1] + sanitizedExpression[2];
        }
    }
}
